import { Car, Wrench, AlertTriangle, CheckCircle2, ShoppingCart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { fmtDate, fmtNum } from "@/lib/format";
import { maintenance, vehicleSales, warrantyEnd } from "@/lib/vehicles";

const TONE = { due: "#C7353B", soon: "#E67E22", ok: "#1F9D55", unknown: "#7F8C8D" };

export default function CustomerVehicles({ customer, onClose }) {
  const { view, t, lang, money } = useApp();
  const nav = useNavigate();
  const list = customer.vehicles || [];
  if (!list.length) return null;
  return (
    <div className="space-y-2" data-testid="customer-vehicles">
      <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Car className="w-5 h-5" />{t("cust_vehicles")}</h3>
      {list.map((v) => {
        const m = maintenance(v, view.sales);
        const sales = vehicleSales(view.sales, v.id);
        return (
          <div key={v.id} className="rounded-xl bg-white border border-border p-3 space-y-2" data-testid={`cust-vehicle-${v.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 rounded bg-[#0B2A5B] text-white font-mono font-bold" dir="ltr" data-testid="cust-vehicle-plate">{v.plate || "—"}</span>
              <b>{v.make} {v.model}</b>{v.year && <span className="text-muted-foreground text-sm">· {v.year}</span>}{v.km && <span className="text-muted-foreground text-sm" dir="ltr">· {fmtNum(v.km)} km</span>}
              <button type="button" onClick={() => { onClose?.(); nav(`/sell?vehicle=${v.id}&customer=${customer.id}`); }} className="ms-auto h-9 px-3 rounded-lg bg-[#1F4E8C] text-white text-xs font-bold flex items-center gap-1" data-testid="cust-vehicle-sell"><ShoppingCart className="w-4 h-4" />{t("sell_for_vehicle")}</button>
            </div>
            <div className="flex items-center gap-2 text-sm rounded-lg p-2" style={{ background: `${TONE[m.status]}14`, color: TONE[m.status] }} data-testid="cust-vehicle-maint">
              {m.status === "due" ? <AlertTriangle className="w-4 h-4" /> : m.status === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <Wrench className="w-4 h-4" />}
              <span className="font-bold">{t(`maint_${m.status}`)}</span>
              {m.lastAt && <span className="text-xs" dir="ltr">· {t("maint_last_oil")}: {fmtDate(m.lastAt, lang, false)}{m.lastKm ? ` @ ${fmtNum(m.lastKm)} km` : ""} → {m.nextDate}{m.nextKm ? ` / ${fmtNum(m.nextKm)} km` : ""}</span>}
            </div>
            {sales.length > 0 && <div className="space-y-1" data-testid="cust-vehicle-history">
              {sales.slice(0, 8).map((s) => (
                <div key={s.id} className="text-xs flex gap-2 border-t border-border pt-1">
                  <span className="text-muted-foreground shrink-0" dir="ltr">{fmtDate(s.created_at, lang, false)}{s.vehicle?.km ? ` · ${fmtNum(s.vehicle.km)} km` : ""}</span>
                  <span className="flex-1 min-w-0 truncate">{s.items.map((i) => `${i.qty}× ${i.name}${i.warranty_months ? ` (${t("warranty_until")} ${warrantyEnd(s.created_at, i.warranty_months).toISOString().slice(0, 10)})` : ""}`).join(", ")}</span>
                  <b className="shrink-0" dir="ltr">{money(s.total)}</b>
                </div>
              ))}
            </div>}
          </div>
        );
      })}
    </div>
  );
}
